import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosClientSignIn } from '../axios/api';

export const fetchCourses = createAsyncThunk(
  'trainingCourses/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get('/api/training-courses');
      return response.data.courses;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch courses');
    }
  }
);

export const fetchCourseById = createAsyncThunk(
  'trainingCourses/fetchById',
  async (courseId, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get(`/api/training-courses/${courseId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch course');
    }
  }
);

export const createCourse = createAsyncThunk(
  'trainingCourses/create',
  async ({ title, description, category, assignedDepartments, isMandatory }, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.post('/api/training-courses', {
        title,
        description,
        category,
        assignedDepartments,
        isMandatory
      });
      return response.data.course;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create course');
    }
  }
);

export const deleteCourse = createAsyncThunk(
  'trainingCourses/delete',
  async (courseId, { rejectWithValue }) => {
    try {
      await axiosClientSignIn.delete(`/api/training-courses/${courseId}`);
      return courseId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete course');
    }
  }
);

export const fetchCourseReport = createAsyncThunk(
  'trainingCourses/fetchReport',
  async (courseId, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get(`/api/training-courses/${courseId}/report`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch course report');
    }
  }
);

const initialState = {
  courses: [],
  currentCourse: null,
  currentVideos: [],
  report: null,
  loading: false,
  creating: false,
  error: null
};

const trainingCourseSlice = createSlice({
  name: 'trainingCourses',
  initialState,
  reducers: {
    clearTrainingCourseError: (state) => {
      state.error = null;
    },
    clearCurrentCourse: (state) => {
      state.currentCourse = null;
      state.currentVideos = [];
    },
    setCourseVideoProgress: (state, action) => {
      const { videoId, progress } = action.payload;
      const video = state.currentVideos.find((v) => v._id === videoId);
      if (video) {
        video.myProgress = { status: progress.status, progressPercent: progress.progressPercent };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCourses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.loading = false;
        state.courses = action.payload;
      })
      .addCase(fetchCourses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchCourseById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCourseById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCourse = action.payload.course;
        state.currentVideos = action.payload.videos;
      })
      .addCase(fetchCourseById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createCourse.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createCourse.fulfilled, (state, action) => {
        state.creating = false;
        state.courses.unshift(action.payload);
      })
      .addCase(createCourse.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload;
      })

      .addCase(deleteCourse.fulfilled, (state, action) => {
        state.courses = state.courses.filter((c) => c._id !== action.payload);
      })

      .addCase(fetchCourseReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCourseReport.fulfilled, (state, action) => {
        state.loading = false;
        state.report = action.payload;
      })
      .addCase(fetchCourseReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearTrainingCourseError, clearCurrentCourse, setCourseVideoProgress } = trainingCourseSlice.actions;
export default trainingCourseSlice.reducer;
